'use client';

import { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils.js';
import { useTheme } from '../providers/ThemeProvider.jsx';
import { ThemeToggle } from '../ui/ThemeToggle.jsx';

/**
 * Header component with split menu animation (opens from both sides)
 * Inspired by the cds2025 Droow template
 */
export function Header({
  logo,
  logoWhite,
  logoAlt = 'Logo',
  navigation = [],
  contactInfo = null,
  socialLinks = [],
  sticky = true,
  className,
  renderLink,
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isHidden, setIsHidden] = useState(false);
  const [menuText, setMenuText] = useState('Menu');
  const { theme } = useTheme();

  useEffect(() => {
    if (!sticky) return;

    const handleScroll = (scrollY) => {
      const currentScrollY = scrollY !== undefined ? scrollY : window.scrollY;

      // Always keep header visible
      setIsHidden(false);

      // Add sticky background when scrolled
      setIsScrolled(currentScrollY > 50);
      setLastScrollY(currentScrollY);
    };

    // Try to find smooth-scrollbar instance
    let smoothScrollbarInstance = null;
    const initSmoothScrollbarListener = async () => {
      try {
        const Scrollbar = (await import('smooth-scrollbar')).default;
        const scrollbarElement = document.querySelector('.dsn-scrollbar');

        if (scrollbarElement) {
          smoothScrollbarInstance = Scrollbar.get(scrollbarElement);

          if (smoothScrollbarInstance) {
            // Listen to smooth-scrollbar scroll events
            smoothScrollbarInstance.addListener((status) => {
              handleScroll(status.offset.y);
            });

            // Initial check
            handleScroll(smoothScrollbarInstance.offset.y);
          }
        }
      } catch (error) {
        // Smooth-scrollbar not available, use regular scroll
        console.log('Using regular scroll listener');
      }
    };

    // Delay to ensure smooth-scrollbar is initialized
    const timer = setTimeout(initSmoothScrollbarListener, 100);

    // Fallback to window scroll
    const fallbackScroll = () => handleScroll(window.scrollY);
    window.addEventListener('scroll', fallbackScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', fallbackScroll);
      if (smoothScrollbarInstance) {
        // Note: smooth-scrollbar doesn't have removeListener, listeners are cleaned on destroy
      }
    };
  }, [sticky, lastScrollY]);

  // Close menu on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('nav-active');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('nav-active');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('nav-active');
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const Link = renderLink || 'a';

  // Determine logo to show based on theme and menu state
  const showWhiteLogo = isMenuOpen || theme === 'dark';
  const currentLogo = showWhiteLogo && logoWhite ? logoWhite : logo;

  const handleMenuHover = () => {
    setMenuText(isMenuOpen ? 'Close' : 'Open');
  };

  const handleMenuLeave = () => {
    setMenuText('Menu');
  };

  return (
    <>
      <header
        className={cn(
          'header',
          isScrolled && !isMenuOpen && 'header--sticky',
          isHidden && !isMenuOpen && 'header--hidden',
          isMenuOpen && 'header--menu-open',
          className
        )}
      >
        <div className="header__container">
          {/* Logo */}
          <Link href="/" className="header__logo">
            {typeof currentLogo === 'string' ? (
              <img
                src={currentLogo}
                alt={logoAlt}
                className="w-[220px] px-2 py-2 h-auto transition-opacity duration-300"
              />
            ) : (
              currentLogo
            )}
          </Link>

          {/* Right side controls */}
          <div className="flex items-center gap-4">
            {/* Theme Toggle */}
            <ThemeToggle className={cn(isMenuOpen && 'relative z-[1000]')} />

            {/* Burger Menu with text */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              onMouseEnter={handleMenuHover}
              onMouseLeave={handleMenuLeave}
              className="menu-icon"
              aria-expanded={isMenuOpen}
            >
              <div className="menu-icon__inner">
                {/* X icon for close */}
                <span className={cn(
                  'menu-icon__close',
                  isMenuOpen ? 'opacity-100' : 'opacity-0'
                )}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </span>

                {/* Burger lines */}
                <span className={cn(
                  'menu-icon__lines',
                  isMenuOpen ? 'opacity-0' : 'opacity-100'
                )}>
                  <span className="menu-icon__line menu-icon__line--left" />
                  <span className="menu-icon__line" />
                  <span className="menu-icon__line menu-icon__line--right" />
                </span>
              </div>

              {/* Text label */}
              <span className="menu-icon__text">
                {menuText}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Split Screen Overlay Menu */}
      <SplitMenu
        isOpen={isMenuOpen}
        navigation={navigation}
        contactInfo={contactInfo}
        socialLinks={socialLinks}
        renderLink={Link}
        onClose={() => setIsMenuOpen(false)}
      />
    </>
  );
}

/**
 * Split Menu Component - Opens from both sides
 */
function SplitMenu({ isOpen, navigation, contactInfo, socialLinks, renderLink: Link, onClose }) {
  // Accordion: Index des aktuell offenen Untermenüs (nur eines gleichzeitig)
  const [openIndex, setOpenIndex] = useState(null);

  // Beim Schließen des Overlays alle Untermenüs zurücksetzen
  useEffect(() => {
    if (!isOpen) setOpenIndex(null);
  }, [isOpen]);

  // Default contact info
  const defaultContactInfo = contactInfo || {
    title: 'Kontakt',
    name: 'Sebastian Nawrot',
    address: 'Dorneystr. 45\n44149 Dortmund',
    phone: '+49 23116730433',
    email: 'sebastian@codeschnipsel.dev',
  };

  // Default social links
  const defaultSocialLinks = socialLinks.length > 0 ? socialLinks : [
    { label: 'LinkedIn', href: '#' },
    { label: 'GitHub', href: '#' },
    { label: 'Twitter', href: '#' },
  ];

  return (
    <div className={cn('split-menu', isOpen && 'split-menu--open')}>
      {/* Left side - Navigation (60%) */}
      <div className="split-menu__nav">
        <div className="split-menu__nav-inner">
          <nav className="split-menu__nav-content">
            {navigation.map((item, index) => (
              <SplitMenuItem
                key={index}
                item={item}
                renderLink={Link}
                onClose={onClose}
                index={index}
                isOpen={isOpen}
                isSubOpen={openIndex === index}
                onToggleSub={() =>
                  setOpenIndex((prev) => (prev === index ? null : index))
                }
              />
            ))}
          </nav>
        </div>
      </div>

      {/* Right side - Contact Info (40%) */}
      <div className="split-menu__content">
        <div className="split-menu__content-inner">
          <address className="split-menu__contact">
            {/* Contact Info */}
            <div className="split-menu__info-block">
              <div className="split-menu__info-title">{defaultContactInfo.title}</div>
              <p className="split-menu__info-text">
                {defaultContactInfo.name}<br />
                {defaultContactInfo.address.split('\n').map((line, i) => (
                  <span key={i}>{line}<br /></span>
                ))}
              </p>
            </div>

            {/* Phone & Email */}
            <div className="split-menu__info-block">
              <div className="split-menu__info-title">Telefon</div>
              <p className="split-menu__info-text">
                <a href={`tel:${defaultContactInfo.phone}`} className="split-menu__link">
                  {defaultContactInfo.phone}
                </a>
              </p>
              <p className="split-menu__info-text">
                <a href={`mailto:${defaultContactInfo.email}`} className="split-menu__link">
                  {defaultContactInfo.email}
                </a>
              </p>
            </div>

            {/* Social Links */}
            <div className="split-menu__info-block">
              <div className="split-menu__info-title">Follow us</div>
              <ul className="split-menu__social">
                {defaultSocialLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="split-menu__social-link"
                    >
                      {link.label}.
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </address>
        </div>
      </div>
    </div>
  );
}

/**
 * Split Menu Item with animation
 */
function SplitMenuItem({ item, renderLink: Link, onClose, index, isOpen, isSubOpen, onToggleSub }) {
  const hasChildren = item.children && item.children.length > 0;

  return (
    <div
      className="split-menu__item"
      style={{
        transitionDelay: isOpen ? `${1.5 + index * 0.15}s` : '0s'
      }}
    >
      {hasChildren ? (
        <>
          <button
            onClick={onToggleSub}
            className="split-menu__nav-link"
            aria-expanded={isSubOpen}
          >
            <span>{item.label}</span>
            <svg
              className={cn(
                'w-6 h-6 ml-3 transition-transform duration-300',
                isSubOpen && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Submenu */}
          <div
            className={cn(
              'split-menu__submenu',
              isSubOpen && 'split-menu__submenu--open'
            )}
          >
            {item.children.map((child, childIndex) => (
              <Link
                key={childIndex}
                href={child.href}
                onClick={onClose}
                className="split-menu__submenu-link"
              >
                {child.label}
              </Link>
            ))}
          </div>
        </>
      ) : (
        <Link
          href={item.href}
          onClick={onClose}
          className="split-menu__nav-link"
        >
          <span>{item.label}</span>
        </Link>
      )}
    </div>
  );
}

export default Header;
